import * as React from 'react';
import { FC, useEffect } from 'react';

import { Box, Header, Icon, InlineAlert, Text } from '@rbilabs/universal-components';
import { isUndefined } from 'lodash-es';
import { isNumber } from 'lodash/fp';
import { useIntl } from 'react-intl';
import { useWindowDimensions } from 'react-native';

import { ActionButtonVariants } from 'components/action-button';
import { CardModal } from 'components/card-modal';
import { CustomDisclaimer } from 'components/offer-disclaimer/offer-disclaimer';
import { WalmartBadgeContainer } from 'components/offer-list-item/styled';
import Picture from 'components/picture';
import { LoyaltyPromotionType } from 'generated/graphql-gateway';
import { useOfferExpiration } from 'hooks/loyalty/use-offer-expiration';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { LoyaltyIncentiveCooldownContent } from 'pages/loyalty/loyalty-incentives-components/loyalty-incentive-cooldown-message';
import { OffersCooldownContainer } from 'pages/loyalty/loyalty-offers/loyalty-offers.styled';
import { useLoyaltyWalmart } from 'pages/loyalty/loyalty-partners/walmart/use-loyalty-walmart';
import { useAuthContext } from 'state/auth';
import { FixableEvaluationErrors, IncentiveEvaluationErrorCodes } from 'state/loyalty/hooks/types';
import {
  getWalmartSeasonalConfigOfferIds,
  isAnyWalmartOffer,
} from 'state/loyalty/hooks/utils/walmart';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import { isAndroid } from 'utils/environment';

import { DISABLE_IN_RESTAURANT_FOR_INVALID_SERVICE_MODE } from '../constants';
import { PointsNeededText, PointsNeededTextDetail } from '../incentive-card/incentive-card.styled';

import {
  AddToCartButton,
  BackgroundImage,
  ButtonContainer,
  ButtonsWrapper,
  DisclaimerText,
  ImageWrapper,
  RedeemInRestaurantButton,
  RemoveFromCartButton,
  RewardDescription,
  RewardDetailsContainer,
  RewardExpiration,
  RewardName,
  StyledDisclaimerContainer,
  StyledIncentiveEvaluationMessages,
  StyledProgressMeter,
  StyledRewardImage,
} from './incentive-details.styled';
import { RewardPoints } from './reward-points';
import { IIncentiveDetailsViewProps } from './types';

const REWARD_IMAGE_HEIGHT = '200px';

export const IncentiveDetailsView: FC<React.PropsWithChildren<IIncentiveDetailsViewProps>> = ({
  id,
  name,
  image,
  imageDescription,
  description,
  moreInfo,
  isOffer,
  pointCost,
  locked,
  remainingPointsNeededForRedemption,
  redemptionPercentage,
  handleRedeemRewardPress,
  handleInRestaurantRedemptionPress,
  errorMessage,
  checkingAvailability,
  backgroundImage,
  canAddToInRestaurantOrder = false,
  canAddToMobileOrder = false,
  showRemoveRewardButton = false,
  handleRemoveRewardsPress,
  incentiveEvaluationResults,
  cooldown,
  onLogIncentiveDetailsEvent,
  endDate,
}) => {
  const { formatMessage } = useIntl();
  const { isAuthenticated } = useAuthContext();
  const { data: loyaltyWalmartData } = useLoyaltyWalmart();

  const { height } = useWindowDimensions();
  const isDesktop = useIsDesktopBreakpoint();

  const hasServiceModeError = (
    evaluationResults: IIncentiveDetailsViewProps['incentiveEvaluationResults']
  ) =>
    !!evaluationResults?.some(
      evaluationResult =>
        evaluationResult.code === IncentiveEvaluationErrorCodes.INVALID_SERVICE_MODE
    );

  const hasBlockingErrors =
    !!errorMessage ||
    (cooldown.isActive && isOffer) ||
    // Disable buttons if Feedback is not included in fixable incentive errors
    incentiveEvaluationResults?.some(
      evaluationResult =>
        !FixableEvaluationErrors.has(evaluationResult.code as IncentiveEvaluationErrorCodes)
    );

  const disableMobileOrderButton =
    hasBlockingErrors || hasServiceModeError(incentiveEvaluationResults);

  const disableInRestaurantOrderButton =
    hasBlockingErrors ||
    (DISABLE_IN_RESTAURANT_FOR_INVALID_SERVICE_MODE &&
      hasServiceModeError(incentiveEvaluationResults));

  useEffect(() => {
    if (!checkingAvailability) {
      const disabledMobile = disableMobileOrderButton || !canAddToMobileOrder;
      const disabledInRestaurant = disableInRestaurantOrderButton || !canAddToInRestaurantOrder;
      const isAvailable = !disabledMobile || !disabledInRestaurant;

      onLogIncentiveDetailsEvent(isAvailable);
    }
  }, [
    canAddToInRestaurantOrder,
    canAddToMobileOrder,
    checkingAvailability,
    disableInRestaurantOrderButton,
    disableMobileOrderButton,
    onLogIncentiveDetailsEvent,
  ]);

  const addToMobileOrderText = formatMessage({ id: 'addToCartCarousel' });

  const inRestaurantRedemptionText = formatMessage({ id: 'redeemInRestaurant' });

  const showWalmartBadge =
    isAnyWalmartOffer({ id }) || getWalmartSeasonalConfigOfferIds(loyaltyWalmartData).includes(id);

  const expiresAt = useOfferExpiration(endDate, isAnyWalmartOffer({ id }));

  return (
    <CardModal
      key="modal"
      alwaysShowHeader
      webAnimate={!(isOffer && isAndroid)} // the loading semantics of offers makes the animation weird.
      headerText={formatMessage({ id: isOffer ? 'offer' : 'reward' })}
      scrollView
      standardPadding
      isLoading={checkingAvailability}
      footer={
        <>
          {!locked && (
            <ButtonContainer>
              {canAddToMobileOrder && !disableMobileOrderButton && (
                <AddToCartButton
                  disabled={disableMobileOrderButton}
                  onPress={handleRedeemRewardPress}
                  isLoading={checkingAvailability}
                  isOffer={isOffer}
                  leftIcon={
                    <Icon
                      name="mi:shopping-bag"
                      width="24px"
                      {...hiddenAccessibilityPlatformProps}
                    />
                  }
                >
                  {addToMobileOrderText}
                </AddToCartButton>
              )}

              {canAddToInRestaurantOrder && !disableInRestaurantOrderButton && (
                <RedeemInRestaurantButton
                  onPress={handleInRestaurantRedemptionPress}
                  isLoading={checkingAvailability}
                  isOffer={isOffer}
                  marginTop="$4"
                  leftIcon={
                    <Icon name="custom:BkCode" width="24px" {...hiddenAccessibilityPlatformProps} />
                  }
                  variant={ActionButtonVariants.OUTLINE}
                >
                  {inRestaurantRedemptionText}
                </RedeemInRestaurantButton>
              )}
            </ButtonContainer>
          )}
          {isAuthenticated && showRemoveRewardButton && (
            <ButtonContainer width={'full'}>
              <RemoveFromCartButton onPress={handleRemoveRewardsPress} marginTop="$8">
                {formatMessage({ id: 'removeRewardsFromCart' })}
              </RemoveFromCartButton>
            </ButtonContainer>
          )}
        </>
      }
    >
      <ImageWrapper>
        {backgroundImage ? (
          <BackgroundImage alt={imageDescription} image={backgroundImage} />
        ) : null}
        {image ? (
          <StyledRewardImage
            alt={imageDescription}
            image={image}
            width={height / (isDesktop ? 2 : 3)}
            {...(!isDesktop && { minHeight: REWARD_IMAGE_HEIGHT })}
          />
        ) : null}
      </ImageWrapper>
      <RewardDetailsContainer>
        {name ? <RewardName>{name}</RewardName> : null}
        {!isUndefined(pointCost) && <RewardPoints pointCost={pointCost} locked={Boolean(locked)} />}
        {description ? <RewardDescription content={description}></RewardDescription> : null}
        {showWalmartBadge && (
          <>
            <Text variant="copyTwo" mt="$4">
              {formatMessage({ id: 'providedBy' })}
            </Text>
            <WalmartBadgeContainer>
              <Picture
                height="40px"
                image={loyaltyWalmartData?.badge?.locale}
                alt={formatMessage({ id: 'walmartOfferBadgeAlt' })}
              />
            </WalmartBadgeContainer>
          </>
        )}
        {locked && (
          <>
            {isNumber(redemptionPercentage) && (
              <StyledProgressMeter percentage={redemptionPercentage} />
            )}
            {isNumber(remainingPointsNeededForRedemption) && (
              <PointsNeededText>
                {formatMessage(
                  { id: 'yourXPointsAway' },
                  {
                    points: (
                      <PointsNeededTextDetail>
                        {remainingPointsNeededForRedemption}
                      </PointsNeededTextDetail>
                    ),
                  }
                )}
              </PointsNeededText>
            )}
          </>
        )}
        {!isOffer && moreInfo && <DisclaimerText content={moreInfo} />}
        {expiresAt && <RewardExpiration>{expiresAt}</RewardExpiration>}
      </RewardDetailsContainer>

      {!!errorMessage && <InlineAlert marginTop="$4" status="info" message={errorMessage} />}

      <ButtonsWrapper
        $errorMessage={!!hasBlockingErrors}
        justifyContent={'center'}
        padding="$0"
        margin="$0"
      ></ButtonsWrapper>

      {incentiveEvaluationResults && !errorMessage && (
        <>
          <Box height="$4" />
          <StyledIncentiveEvaluationMessages
            incentiveEvaluationResults={incentiveEvaluationResults}
            hideIcon
          />
        </>
      )}
      {cooldown.isActive && isOffer && (
        <OffersCooldownContainer>
          <Box flexDirection="row" alignItems="center" paddingX="$2">
            <LoyaltyIncentiveCooldownContent
              incentiveType={LoyaltyPromotionType.OFFER}
              availableAfter={cooldown.endsAt}
            />
          </Box>
        </OffersCooldownContainer>
      )}
      {isOffer && moreInfo && (
        <StyledDisclaimerContainer style={{ flex: 0 }} marginTop="$6">
          <Header variant={'headerThree'}>{formatMessage({ id: 'offerTerms' })}</Header>
          <CustomDisclaimer
            content={moreInfo}
            textProps={{
              textAlign: 'left',
            }}
          />
        </StyledDisclaimerContainer>
      )}
    </CardModal>
  );
};
